/**
 * Cookiebot as the CMP provider behind the vendor neutral consent manager.
 *
 * This is the one module that knows Cookiebot exists. It translates Cookiebot's signal into a
 * {@link ConsentDecision} and hands it to the consent manager in `@tv2/lib-consent`; every consumer
 * reads the manager and therefore stays vendor agnostic — the same code works on Smart TV, where
 * OneTrust feeds the same manager.
 *
 * Judging the recorded decision is the provider's job too, since it knows what its template
 * contains. For the web those judgements live next door and are connected here: whether advertising
 * identifiers may be handed out (`advid/advidConsent.ts` — full consent, and the advid deleted
 * otherwise) and the one-word consent status for tracking (`consentStatus.ts`).
 *
 * The Cookiebot script itself is loaded by `CMPProvider` (`@tv2/ui-cmp/Cookiebot`). This module only
 * listens.
 *
 * ## Where the answer comes from
 *
 * 1. `window.Cookiebot.consent` once the script has run and the user has an answer on record.
 * 2. The `CookieConsent` cookie, as a stand-in while the script is still loading — or if it never
 *    loads, which happens behind content blockers. It is the user's real answer, written by
 *    Cookiebot itself, so honouring it is better than treating a blocked script as a rejection.
 * 3. Nothing at all, which the manager treats as declined. Fail-closed is the point.
 *
 * ## Changing consent mid-session
 *
 * A loaded third-party script cannot be unloaded, so the only way to make the loaded scripts match a
 * new consent is to reload. Only a change relative to the answer *Cookiebot itself* gave first this
 * session triggers that: the cookie read in step 2 is a stand-in and is allowed to be corrected
 * silently, otherwise a cookie that parsed differently from the script would reload the page on
 * every single load.
 */
import {
  ALL_CONSENT_DENIED,
  ALL_CONSENT_GRANTED,
  type ConsentDecision,
  isConsentDecisionResolved,
  isSameConsentDecision,
  reloadForConsentChange,
  setConsentDecision,
  setConsentIntegrationEnabled,
  setTcfConsentString,
} from '@tv2/lib-consent/consentManager';
import { connectAdvidConsent } from './advid/advidConsent';
import { connectConsentStatus } from './consentStatus';

/** The consent flags Cookiebot exposes. `necessary` is exempt from consent and therefore unused. */
type CookiebotConsent = {
  marketing?: boolean;
  necessary?: boolean;
  preferences?: boolean;
  statistics?: boolean;
};

type Cookiebot = {
  hasResponse?: boolean;
  consent?: CookiebotConsent;
  /** Consent given once for a group of domains, used when the per-domain consent is absent. */
  bulkconsent?: CookiebotConsent;
};

type TcfApi = (
  command: 'addEventListener' | 'removeEventListener',
  version: number,
  callback: (tcData: { tcString?: string; listenerId?: number }, success: boolean) => void,
  listenerId?: number
) => void;

type CookiebotWindow = Window & {
  Cookiebot?: Cookiebot;
  __tcfapi?: TcfApi;
};

/** The events Cookiebot fires when it has an answer, or when the user changes it. */
const COOKIEBOT_EVENTS = [
  'CookiebotOnConsentReady',
  'CookiebotOnAccept',
  'CookiebotOnDecline',
] as const;

/** Cookiebot's own record of the user's answer, readable before its script has run. */
const CONSENT_COOKIE = 'CookieConsent';

/**
 * The value Cookiebot writes when consent is not required in the visitor's region. It is an
 * unconditional yes, not a missing answer.
 */
const CONSENT_NOT_REQUIRED = '-1';

/** What "consent not required" grants: every category Cookiebot has, none it does not. */
const COOKIEBOT_NOT_REQUIRED: Pick<ConsentDecision, 'Preferences' | 'Statistics' | 'Marketing'> = {
  Preferences: true,
  Statistics: true,
  Marketing: true,
};

/** The answer Cookiebot itself has given this session, as opposed to the cookie stand-in. */
let cmpDecision: ConsentDecision | null = null;

/**
 * Starts feeding the consent manager from Cookiebot. Returns a function that stops listening.
 *
 * Call once per app session, from the client. Passing no script url means no CMP is configured, in
 * which case every category is granted — that is the behaviour from before the consent manager
 * existed, and it keeps a misconfigured environment usable instead of silently switching the site
 * off.
 */
export function startCookiebotConsent(cmpScriptUrl: string | undefined): () => void {
  if (typeof window === 'undefined') return () => {};

  // Connected before anything is recorded, so the status reflects the no-CMP case as well.
  const disconnectConsentStatus = connectConsentStatus();

  if (!cmpScriptUrl) {
    setConsentIntegrationEnabled(false);
    setConsentDecision(ALL_CONSENT_GRANTED);
    return disconnectConsentStatus;
  }

  setConsentIntegrationEnabled(true);

  // Cookiebot only creates `__tcfapi` once its script has run, which may be after this starts. So
  // this is tried now, and again on every Cookiebot event until it attaches.
  let stopTcfListener = startTcfStringListener();

  const onCookiebotEvent = () => {
    const decision = readCookiebotDecision();
    if (decision) recordCmpDecision(decision);
    if (!stopTcfListener) stopTcfListener = startTcfStringListener();
  };

  for (const event of COOKIEBOT_EVENTS) {
    window.addEventListener(event, onCookiebotEvent);
  }

  // The script may already have run by the time this starts, in which case no further event is
  // coming and the answer is simply there to be read.
  onCookiebotEvent();

  // Otherwise fall back to Cookiebot's own cookie, so a visitor whose answer is on record is not
  // treated as having declined while the script loads — or forever, if it is blocked.
  if (!isConsentDecisionResolved()) {
    const cookieDecision = readConsentCookieDecision();
    if (cookieDecision) setConsentDecision(cookieDecision);
  }

  // Connected only now that whatever answer is on record has been read. The advid rule deletes the
  // value whenever consent is not full, and judging an empty manager on every page load would
  // delete the advid of a user who has accepted everything — and mint them a new one each time.
  // Until this point the manager fails closed on its own, so nothing is released early.
  const disconnectAdvidConsent = connectAdvidConsent();

  return () => {
    for (const event of COOKIEBOT_EVENTS) {
      window.removeEventListener(event, onCookiebotEvent);
    }
    stopTcfListener?.();
    disconnectAdvidConsent();
    disconnectConsentStatus();
  };
}

/**
 * Records an answer that came from Cookiebot itself. The first one establishes what the session was
 * loaded under; a later one that differs means the user changed their mind in the preference
 * center, which requires a reload for the loaded scripts to match.
 */
function recordCmpDecision(decision: ConsentDecision): void {
  if (!cmpDecision) {
    cmpDecision = decision;
    setConsentDecision(decision);
    return;
  }

  if (isSameConsentDecision(cmpDecision, decision)) return;

  cmpDecision = decision;
  setConsentDecision(decision);
  reloadForConsentChange();
}

/**
 * The decision Cookiebot currently holds, or `null` while the user has no answer on record. Falls
 * back to the bulk consent given for a group of domains when this domain has none of its own.
 */
function readCookiebotDecision(): ConsentDecision | null {
  const cookiebot = (window as CookiebotWindow).Cookiebot;
  if (!cookiebot?.hasResponse) return null;
  return toDecision(cookiebot.consent) ?? toDecision(cookiebot.bulkconsent);
}

function toDecision(consent: CookiebotConsent | undefined): ConsentDecision | null {
  if (!consent || typeof consent !== 'object') return null;
  // Cookiebot exposes no IAB TCF groups, so those stay declined.
  return {
    ...ALL_CONSENT_DENIED,
    Preferences: consent.preferences === true,
    Statistics: consent.statistics === true,
    Marketing: consent.marketing === true,
  };
}

/**
 * Reads a cookie value whole.
 *
 * Deliberately not `cookieParser.getCookie`: that one matches on a name prefix and keeps only the
 * part before the first `=`, and the consent cookie is a base64-bearing object literal that both
 * rules would mangle.
 */
function readCookie(name: string): string | null {
  const entry = document.cookie.split(';').find(c => c.trimStart().startsWith(`${name}=`));
  if (!entry) return null;
  const [, ...value] = entry.trimStart().split('=');
  return value.join('=');
}

/** The decision recorded in Cookiebot's own cookie, readable before its script has run. */
function readConsentCookieDecision(): ConsentDecision | null {
  const cookie = readCookie(CONSENT_COOKIE);
  if (!cookie) return null;
  if (cookie === CONSENT_NOT_REQUIRED) return { ...ALL_CONSENT_DENIED, ...COOKIEBOT_NOT_REQUIRED };

  try {
    // The cookie holds a JavaScript object literal rather than JSON: unquoted keys and single
    // quoted strings.
    const parsed = JSON.parse(
      decodeURIComponent(cookie)
        .replace(/(\w+):+/g, '"$1":')
        .replace(/'/g, '"')
    );
    return toDecision(parsed);
  } catch {
    return null;
  }
}

/**
 * Keeps the raw IAB TCF consent string up to date for consumers that pass the full consent record
 * onward. Returns a function that stops listening, or `null` when there was nothing to attach to
 * yet: Cookiebot only exposes `__tcfapi` when its TCF integration is enabled and once its script
 * has run, so the caller tries again on the next Cookiebot event.
 */
function startTcfStringListener(): (() => void) | null {
  const tcfApi = (window as CookiebotWindow).__tcfapi;
  if (typeof tcfApi !== 'function') return null;

  let listenerId: number | undefined;
  try {
    tcfApi('addEventListener', 2, (tcData, success) => {
      if (!success) return;
      listenerId = tcData.listenerId;
      setTcfConsentString(tcData.tcString || undefined);
    });
  } catch {
    // A CMP that cannot answer leaves the string undefined, which consumers omit entirely.
    return null;
  }

  return () => {
    if (listenerId === undefined) return;
    try {
      tcfApi('removeEventListener', 2, () => {}, listenerId);
    } catch {
      // Nothing to do — the page is going away anyway.
    }
  };
}

/** TEST ONLY: forget the answer Cookiebot gave, so each case starts a fresh session. */
export function __resetCookiebotConsentForTests(): void {
  cmpDecision = null;
}
